<template>
  <div>
    <!-- WhatsApp/Telegram Input -->
    <q-card dark flat v-if="sessionType === 'o'" class="q-mb-lg">
      <q-card-section>
        <q-input
          ref="contactInputRef"
          dark
          filled
          v-model="numberName"
          :label="lang === 'de' ? 'WhatsApp/Telegram' : 'WhatsApp/Telegram'"
          :rules="[
            (val) => !!(val && val.trim().length >= 3) || requiredMessage
          ]"
          :hint="lang === 'de' ? 'Handynummer (für WhatsApp) oder Telegram Namen eingeben' : 'Enter cell phone number (for WhatsApp) or Telegram name'"
          placeholder="+49 1525 2714335"
          lazy-rules
          @blur="updateDB()"
          @keydown.enter.prevent
        />
      </q-card-section>
    </q-card>

    <q-banner
      v-if="orderContextError"
      dark
      rounded
      dense
      class="q-mb-lg"
    >
      {{ orderContextError }}
    </q-banner>

    <!-- Payment Card -->
    <q-card v-if="!isAppointmentUnavailable && !orderContextError" dark flat class="relative">
      <q-card-section>
        <div v-if="isPreparingOrderContext" class="row justify-center q-pa-md">
          <q-spinner color="primary" size="32px" />
        </div>

        <!-- Stripe Buttons -->
        <stripe-buy-button
          v-if="canRenderStripeButton && price === 100"
          buy-button-id="buy_btn_1SQNsWLLLx7K96FRW7XGX9OR"
          publishable-key="pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq"
          :client-reference-id="reference"
          :class="{'btn-disable': isPaymentBlocked}"
        />
        <stripe-buy-button
          v-if="canRenderStripeButton && price === 150"
          buy-button-id="buy_btn_1SQNuZLLLx7K96FRJ85DcG2a"
          publishable-key="pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq"
          :client-reference-id="reference"
          :class="{'btn-disable': isPaymentBlocked}"
        />
        <stripe-buy-button
          v-if="canRenderStripeButton && price === 50"
          buy-button-id="buy_btn_1SOCmbLLLx7K96FRCLXMuffI"
          publishable-key="pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq"
          :client-reference-id="reference"
          :class="{'btn-disable': isPaymentBlocked}"
        />
      </q-card-section>

      <q-card-section v-if="false">
        <!-- PayPal Button -->
        <paypal-button
          :amount="sessionType === 'o' ? 50 + (restBetrag != null ? restBetrag : 50) : 50"
          :reference="reference"
          :class="{'btn-disable': isPaymentBlocked}"
          @update:is-loading="(val) => emit('update:isLoading', val)"
        />
      </q-card-section>
      <!-- Overlay blockt Stripe + PayPal -->
      <div
        v-if="isPaymentBlocked"
        class="click-guard"
        role="button"
        tabindex="0"
        :aria-label="guardMessage"
        @click="handleGuardClick"
        @keydown.enter.prevent="handleGuardClick"
        @keydown.space.prevent="handleGuardClick"
      >
        <!-- Optionaler Hinweistext -->
        <!-- <div class="guard-hint">{{ requiredMessage }}</div> -->
      </div>
    </q-card>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, ref, defineEmits, onMounted, watch } from 'vue'
import type { QInput } from 'quasar'
import PaypalButton from '@/components/PaypalButton.vue'

// Props
const props = defineProps<{
  appointmentID: string
  duration: number
  time: string
  sessionType: string
  restBetrag?: number
  startDate?: Date
  studioMalbert?: boolean
  studioDortmund?: boolean
}>()

/* eslint-disable */
const emit = defineEmits<{
  (e: 'update:isLoading', value: boolean): boolean
}>()

const numberName = ref('')
const contactInputRef = ref<QInput | null>(null)
const orderContextReady = ref(false)
const isPreparingOrderContext = ref(false)
const orderContextError = ref<string | null>(null)
const isAppointmentUnavailable = ref(false)

// 🌍 Sprache anhand der URL bestimmen
const path = window.location.pathname
const lang = path.startsWith('/en/') ? 'en' : 'de'

// Dynamische Fehlermeldung
const requiredMessage = computed(() =>
  lang === 'de'
    ? 'Deine Handynummer oder deinen Telegram-Namen eingeben'
    : 'Enter your cell number or Telegram name'
)

// Computeds
const needsContact = computed(() => props.sessionType === 'o')
const missingContact = computed(() => numberName.value.length < 3)
const isGuardActive = computed(() => needsContact.value && missingContact.value)
const canRenderStripeButton = computed(() => orderContextReady.value && !isPreparingOrderContext.value && !orderContextError.value && !isAppointmentUnavailable.value)
const isOrderContextBlocked = computed(() => !canRenderStripeButton.value)
const isPaymentBlocked = computed(() => isGuardActive.value || isOrderContextBlocked.value)
const reference = computed(() => `${props.appointmentID}__${props.time}`)

const sessionTyp = computed(() => {
  if (props.sessionType === 'o') return 'Plauderstunde (online)'
  if (props.sessionType === 'p') return 'Plauderstunde'
  if (props.sessionType === 's' && (props.studioMalbert || props.studioDortmund)) return 'Session (Studio Malbert)'
  if (props.sessionType === 's') return 'Session'
  return props.sessionType
})

const guardMessage = computed(() => {
  if (isGuardActive.value) return requiredMessage.value
  if (orderContextError.value) return orderContextError.value
  return lang === 'de'
    ? 'Zahlung wird vorbereitet'
    : 'Payment is being prepared'
})

const price = computed<number>(() => {
  if (props.sessionType === 'o') {
    if (props.restBetrag == null || props.restBetrag == 50) return 100
    return 150
  }
  return 50
})

onMounted(() => {
  void ensureOrderContext()
})

watch(
  () => [
    props.appointmentID,
    props.time,
    props.startDate?.getTime(),
    props.duration,
    props.sessionType,
    props.restBetrag,
    props.studioMalbert,
    props.studioDortmund,
  ],
  () => {
    void ensureOrderContext()
  }
)

// Overlay-Klick → Fehler anzeigen + Scroll
function handleGuardClick() {
  if (isGuardActive.value) {
    contactInputRef.value?.validate()
    const el = contactInputRef.value?.$el as HTMLElement | undefined
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    requestAnimationFrame(() => contactInputRef.value?.focus())
    return
  }

  if (orderContextError.value) {
    void ensureOrderContext()
  }
}

async function ensureOrderContext(extraPayload: Record<string, unknown> = {}) {
  orderContextReady.value = false
  orderContextError.value = null
  isAppointmentUnavailable.value = false

  if (!props.startDate || Number.isNaN(props.startDate.getTime())) {
    orderContextError.value = lang === 'de'
      ? 'Der Termin konnte nicht vorbereitet werden. Bitte wähle den Termin neu aus.'
      : 'The appointment could not be prepared. Please select the appointment again.'
    return
  }

  const endDate = new Date(props.startDate.getTime() + props.duration * 60_000)
  if (Number.isNaN(endDate.getTime())) {
    orderContextError.value = lang === 'de'
      ? 'Die Termindauer ist ungültig. Bitte wähle den Termin neu aus.'
      : 'The appointment duration is invalid. Please select the appointment again.'
    return
  }

  const payload = {
    reference: reference.value,
    appointmentId: props.appointmentID,
    startDate: props.startDate.toISOString(),
    endDate: endDate.toISOString(),
    restBetrag: props.restBetrag,
    sessionTyp: sessionTyp.value,
    studioMalbert: Boolean(props.studioMalbert || props.studioDortmund),
    studioDortmund: Boolean(props.studioMalbert || props.studioDortmund),
    studio: props.studioMalbert || props.studioDortmund ? 'dortmund' : undefined,
    preserveExisting: true,
    ...extraPayload,
  }

  isPreparingOrderContext.value = true
  try {
    const response = await fetch('https://setordernote-3uz45qef3a-ey.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      if (response.status === 409 && data?.code === 'appointment_unavailable') {
        isAppointmentUnavailable.value = true
        orderContextError.value = lang === 'de'
          ? 'Dieser Termin ist leider nicht mehr verfügbar. Bitte wähle einen neuen Termin.'
          : 'This appointment is no longer available. Please select a new appointment.'
        return
      }

      if (response.status === 400 && data?.code === 'invalid_appointment_time') {
        orderContextError.value = lang === 'de'
          ? 'Die Termindaten sind ungültig. Bitte wähle den Termin neu aus.'
          : 'The appointment details are invalid. Please select the appointment again.'
        return
      }

      throw new Error(`setOrderNote failed: ${response.status}`)
    }

    orderContextReady.value = true
  } catch(e) {
    console.log(e)
    orderContextError.value = lang === 'de'
      ? 'Die Zahlung konnte nicht vorbereitet werden. Bitte lade die Seite neu.'
      : 'Payment could not be prepared. Please reload the page.'
  } finally {
    isPreparingOrderContext.value = false
  }
}

async function updateDB() {
  if (isGuardActive.value) {
    return
  }
  await ensureOrderContext({ whatsappTelegram: numberName.value.trim() })
}

</script>

<style scoped>
.relative {
  position: relative;
}

.click-guard {
  position: absolute;
  inset: 0;
  z-index: 150;
  cursor: not-allowed;
  background: transparent;
}

.btn-disable {
  opacity: 0.5;
  cursor: not-allowed;
}

.guard-hint {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 8px 12px;
  border-radius: 6px;
  backdrop-filter: blur(2px);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 14px;
  pointer-events: none;
}
</style>
